import { LitElement, html, internalProperty, property } from 'lit-element'
import { contextualize, cutlassEvent } from '@polleverywhere/cutlass'
import { ifDefined } from 'lit-html/directives/if-defined'
import { register } from '@polleverywhere/hmr-custom-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { defineElementSlot } from '@polleverywhere/element-helpers'
import '@polleverywhere/icon-element'
import '@polleverywhere/pe-menu'
import './breadcrumb-item.scss'

/**
 * Renders the individual breadcrumb within the `<pe-breadcrumb>` element.
 * Breadcrumbs may be rendered with an optional icon, name (as unique
 * identifier) or with an `href` attribute to let it behave similar to normal
 * `<a>` links.
 *
 * @element pe-breadcrumb-item
 *
 * @slot - Breadcrumb label
 *
 * @prop {boolean} collapsed=false - Indicates that this breadcrumb is collapsed
 *  into another sibling's dropdown menu and is thus hidden from display.
 * @prop {boolean} highlight=false - Render a highlight around the breadcrumb
 *  label to draw more attention to it.
 * @prop {string} href='' - When assigned, breadcrumb will act as a normal `<a>`
 *  link and redirect the user to the URL assigned.
 * @prop {string} icon - Render an icon next to the breadcrumb label.
 * @prop {string} name - Apply a unique identifier to this breadcrumb. When
 *  breadcrumb is acted upon and no `href` property is assigned, `action` event
 *  is emitted with this value instead of the full label.
 * @prop {boolean} isFirst=false - Is true if this breadcrumb is the first item
 *  in the list. This should never be explicitly set by outside consumers but
 *  only by the parent pe-breadcrumb element itself.
 * @prop {boolean} isLast=false - Is true if this breadcrumb is the last item in
 *  the list. This should never be explicitly set by outside consumers but only
 *  by the parent pe-breadcrumb element itself.
 * @prop {Array} menu=[] - Never explicitly set by outside consumers, this holds
 *  the list of sibling items that are rendered into a dropdown menu.
 * @prop {string} separator - Never explicitly set by outside consumers, this
 *  separator is rendered after itself.
 * @prop {string} label - (readonly) Returns the breadcrumb text label.
 *
 * @fires {Object} action - Fires when breadcrumb is acted upon.
 */
export default class BreadcrumbItem extends LitElement {
  static get tagName () { return 'pe-breadcrumb-item' }

  @property({ type: Boolean, reflect: true })
  collapsed = false

  @property({ type: Boolean, reflect: true })
  highlight = false

  @property({ type: String })
  href = ''

  @property({ type: String })
  icon

  @property({ type: String })
  name

  @internalProperty({ type: Boolean })
  isFirst = false

  @internalProperty({ type: Boolean })
  isLast = false

  @internalProperty({ type: Array })
  menu = []

  @internalProperty({ type: String })
  separator

  get label () {
    return this.slots().map(node => node.textContent.trim()).join('')
  }

  constructor () {
    super()

    // contextualize cutlass events
    contextualize(this, this.tagName)
  }

  connectedCallback () {
    super.connectedCallback()
    defineElementSlot(this)
  }

  createRenderRoot () { return this }

  render () {
    if (this.isLast) {
      return html`
      <span class=${bem('leaf')} title=${this.label}>
        ${renderHighlight.call(this)}
        ${renderLabel.call(this)}
      </span>`
    }

    return html`
    <a @click=${handleClick}
        class=${bem('link')}
        aria-label=${this.label}
        href=${this.href}
        name=${ifDefined(this.name)}
        title=${this.label}>
      ${renderHighlight.call(this)}
      ${renderLabel.call(this)}
    </a>
    ${renderSeparator.call(this)}
    ${renderMenu.call(this)}
    `
  }
}

function renderHighlight () {
  return html`
  <span class=${bem('highlight', { show: this.highlight })}></span>`
}

function renderLabel () {
  const mods = {
    wrap: !this.isFirst,
    leaf: this.isLast
  }

  return html`
  ${this.icon ? html`
  <icon-element
      class=${bem('icon')}
      type='glyph'
      name=${this.icon}
  ></icon-element>` : ''}
  <span class=${bem('label', mods)}>${this.slots()}</span>`
}

function renderSeparator () {
  const separator = this.separator?.trim()
  const mods = { empty: !separator }
  return html`<span class=${bem('separator', mods)}>${separator}</span>`
}

function renderMenu () {
  if (!this.menu.length) return

  return html`
  <pe-menu>
    <button slot='control' class=${bem('menu-control')}>...</button>
    ${this.menu.map(item => html`
    <pe-menu-item
        icon=${ifDefined(item.icon)}
        name=${ifDefined(item.name)}
    >${item.label}</pe-menu-item>`)}
  </pe-menu>
  ${renderSeparator.call(this)}`
}

function handleClick (e) {
  const target = e.currentTarget

  // If link has an href attr, let them act as normal links.
  if (target.getAttribute('href')) return

  e.preventDefault()
  this.dispatchEvent(new CustomEvent('action', {
    bubbles: true,
    detail: {
      name: target.name || target.getAttribute('aria-label')
    }
  }))

  cutlassEvent(e.target, 'click')
}

const bem = bemBlock(BreadcrumbItem.tagName)
register(module, BreadcrumbItem.tagName, BreadcrumbItem)
